import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import ConfirmationModal from "../modals/ConfirmationModal";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  ClientAccountView,
  ClientBrandView,
  useCreateClientBrandMutation,
  useDeleteClientBrandMutation,
  useUpdateClientBrandMutation,
} from "../../store/apis/clientRegistryApi";

interface Props {
  account: ClientAccountView;
  countries: { code: string; name: string }[];
  canEdit: boolean;
}

/**
 * Las marcas de una cuenta.
 *
 * El número de marca se puede escribir a mano en el alta, y no es un descuido: la misma
 * marca se repite entre países con el MISMO número —Reebok es la 12 de Tailored en DE,
 * ES, PT, FR e IT—, así que al añadir el segundo país hay que poder repetir el 12 en vez
 * de dejar que el contador dé un 13. Si se deja vacío, el servidor asigna el siguiente.
 *
 * El código (10410ES) no se guarda: se calcula a partir de cuenta + marca + país. Por eso
 * aquí solo se muestra.
 */
const BrandsPanel: React.FC<Props> = ({ account, countries, canEdit }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [createBrand, { isLoading: isCreating }] =
    useCreateClientBrandMutation();
  const [updateBrand, { isLoading: isUpdating }] =
    useUpdateClientBrandMutation();
  const [deleteBrand, { isLoading: isDeleting }] =
    useDeleteClientBrandMutation();

  const [editing, setEditing] = useState<ClientBrandView | null>(null);
  const [isNewOpen, setIsNewOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ClientBrandView | null>(
    null
  );

  const [name, setName] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [brandNumber, setBrandNumber] = useState("");
  const [active, setActive] = useState(true);

  const openNew = () => {
    setName("");
    setCountryCode("");
    setBrandNumber("");
    setActive(true);
    setIsNewOpen(true);
  };

  const openEdit = (brand: ClientBrandView) => {
    setName(brand.name);
    setCountryCode(brand.countryCode);
    setBrandNumber(String(brand.brandNumber));
    setActive(brand.active);
    setEditing(brand);
  };

  const closeDialog = () => {
    setIsNewOpen(false);
    setEditing(null);
  };

  const toast = (message: string, severity: "success" | "error") =>
    dispatch(setToastMessage({ message, severity }));

  const errorMessage = (error: unknown) => {
    const data = (error as { data?: { message?: string } })?.data;
    return (
      data?.message ??
      t("clientRegistry.brands.saveFailed", "No se pudo guardar la marca")
    );
  };

  const handleCreate = async () => {
    try {
      await createBrand({
        accountId: account.id,
        body: {
          name: name.trim(),
          countryCode,
          // Vacío = que lo asigne el contador del servidor.
          brandNumber: brandNumber.trim() ? Number(brandNumber) : undefined,
        },
      }).unwrap();
      toast(t("clientRegistry.brands.created", "Marca añadida"), "success");
      closeDialog();
    } catch (error: unknown) {
      toast(errorMessage(error), "error");
    }
  };

  const handleUpdate = async () => {
    if (!editing) return;
    try {
      await updateBrand({
        brandId: editing.id,
        body: { name: name.trim(), countryCode, active },
      }).unwrap();
      toast(t("clientRegistry.brands.updated", "Marca actualizada"), "success");
      closeDialog();
    } catch (error: unknown) {
      toast(errorMessage(error), "error");
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteBrand(pendingDelete.id).unwrap();
      toast(t("clientRegistry.brands.deleted", "Marca borrada"), "success");
    } catch (error: unknown) {
      toast(errorMessage(error), "error");
    } finally {
      setPendingDelete(null);
    }
  };

  const isDialogOpen = isNewOpen || editing !== null;
  const canSubmit = name.trim().length > 0 && countryCode.length > 0;

  return (
    <Box sx={{ py: 2, pl: { xs: 2, md: 6 }, pr: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {t("clientRegistry.brands.title", "Marcas")} ({account.brands.length})
        </Typography>
        {canEdit && (
          <Button size="small" startIcon={<AddIcon />} onClick={openNew}>
            {t("clientRegistry.brands.add", "Añadir marca")}
          </Button>
        )}
      </Stack>

      {account.brands.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t(
            "clientRegistry.brands.empty",
            "Esta cuenta no tiene marcas. El número de propuesta usará solo el nombre del cliente."
          )}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("clientRegistry.brands.code", "Código")}</TableCell>
              <TableCell>
                {t("clientRegistry.brands.number", "Nº marca")}
              </TableCell>
              <TableCell>{t("clientRegistry.brands.name", "Marca")}</TableCell>
              <TableCell>
                {t("clientRegistry.brands.country", "País")}
              </TableCell>
              <TableCell>
                {t("clientRegistry.brands.active", "Activa")}
              </TableCell>
              {canEdit && <TableCell align="right" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {account.brands.map((brand) => (
              <TableRow key={brand.id} hover>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", fontWeight: 600 }}
                  >
                    {brand.brandCode}
                  </Typography>
                </TableCell>
                <TableCell>{brand.brandNumber}</TableCell>
                <TableCell>{brand.name}</TableCell>
                <TableCell>
                  {brand.countryName} ({brand.countryCode})
                </TableCell>
                <TableCell>
                  {brand.active ? (
                    <Chip
                      size="small"
                      color="success"
                      label={t("clientRegistry.yes", "Sí")}
                    />
                  ) : (
                    <Chip size="small" label={t("clientRegistry.no", "No")} />
                  )}
                </TableCell>
                {canEdit && (
                  <TableCell align="right">
                    <Tooltip title={t("clientRegistry.edit", "Editar")}>
                      <IconButton
                        size="small"
                        onClick={() => openEdit(brand)}
                        disabled={isDeleting}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("clientRegistry.delete", "Borrar")}>
                      <IconButton
                        size="small"
                        onClick={() => setPendingDelete(brand)}
                        disabled={isDeleting}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={isDialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {editing
            ? t("clientRegistry.brands.editTitle", "Editar marca")
            : t("clientRegistry.brands.newTitle", "Nueva marca")}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField
              label={t("clientRegistry.brands.name", "Marca")}
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
              autoFocus
            />

            <FormControl fullWidth>
              <InputLabel>
                {t("clientRegistry.brands.country", "País")}
              </InputLabel>
              <Select
                value={countryCode}
                label={t("clientRegistry.brands.country", "País")}
                onChange={(event) => setCountryCode(event.target.value)}
              >
                {countries.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name} ({country.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {editing ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={(event) => setActive(event.target.checked)}
                  />
                }
                label={t("clientRegistry.brands.active", "Activa")}
              />
            ) : (
              <TextField
                label={t(
                  "clientRegistry.brands.numberOptional",
                  "Nº de marca (opcional)"
                )}
                value={brandNumber}
                onChange={(event) => setBrandNumber(event.target.value)}
                type="number"
                fullWidth
                helperText={t(
                  "clientRegistry.brands.numberHelp",
                  "Déjalo vacío y se asigna el siguiente. Rellénalo solo para repetir la misma marca en otro país, que lleva el mismo número."
                )}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>
            {t("clientRegistry.cancel", "Cancelar")}
          </Button>
          <Button
            variant="contained"
            onClick={editing ? handleUpdate : handleCreate}
            disabled={!canSubmit || isCreating || isUpdating}
          >
            {t("clientRegistry.save", "Guardar")}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        open={pendingDelete !== null}
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title={t("clientRegistry.brands.deleteTitle", "Borrar marca")}
        message={t(
          "clientRegistry.brands.deleteMessage",
          'Se borrará la marca "{{name}}" ({{code}}). El número no se reutiliza: queda un hueco, igual que en la hoja de cálculo.',
          {
            name: pendingDelete?.name ?? "",
            code: pendingDelete?.brandCode ?? "",
          }
        )}
        confirmLabel={t("clientRegistry.delete", "Borrar")}
      />
    </Box>
  );
};

export default BrandsPanel;
